import { getAPIManagerInstance } from '../../../api/apiManager';
import { awsConfig } from '../../../aws-config/aws-config';
import {
  clearEmail,
  clearPersistedState,
  clearTokens,
  getEmail,
  saveEmail,
  saveTokens,
} from '../../../common/tokenPersistence';
import {
  isNullUndefinedOrEmptyObject,
  isNullUndefinedOrEmptyString,
} from '../../../common/utils';
import {
  logout,
  setCognitoEmailAddress,
  setCognitoUsername,
  setUserDetails,
  setAuthCodeRequired,
  setAccessToken,
  setIdToken,
  setProgressMessage,
  setErrorMessage,
  workStarted,
  workFailed,
  workSucceeded,
  setCreateAccountEmailAddress,
  setIsAccountCreationCompleted,
  setIsUserLoginCompleted,
  setSendAuthCodeInProgress,
  setSubmitAuthCodeInProgress,
} from './authSlice';
import {
  signIn,
  signOut,
  confirmSignIn,
  fetchAuthSession,
} from 'aws-amplify/auth';

// Thunks - these are asynchronous functions that interact with reducers in predictable ways
// All api interactions should be performed via a "thunk" so that api request state can
// be updated in the Redux store rather than at the component level

export const validateCognitoEmailAddress = (emailAddress) => {
  return async (dispatch, _getState, api) => {
    dispatch(workStarted());
    try {
      const result =
        await api.getCognitoUserIdByEmailAddressAsync(emailAddress);

      if (Object.keys(result).length !== 0) {
        dispatch(workFailed());
        return {
          success: false,
          userExists: true,
          validationError: false,
        };
      } else {
        dispatch(setCreateAccountEmailAddress(emailAddress));
        dispatch(workSucceeded());
        return {
          success: true,
          userExists: false,
          validationError: false,
        };
      }
    } catch (err) {
      dispatch(workFailed());
      dispatch(
        setErrorMessage(
          'An unexpected error occurred while attempting validate the provided email address.'
        )
      );
      return {
        success: false,
        userExists: false,
        validationError: true,
      };
    }
  };
};

export const retrieveCognitoUserAsync = (emailAddress) => {
  return async (dispatch, _getState, api) => {
    dispatch(
      setProgressMessage({
        message: 'Initializing login procress...',
      })
    );
    dispatch(logout());
    clearPersistedState();
    dispatch(
      setProgressMessage({
        message: 'Retrieving associated user identity...',
      })
    );
    dispatch(workStarted());
    try {
      // Check email address validity
      if (isNullUndefinedOrEmptyString(emailAddress)) {
        dispatch(
          setErrorMessage({
            message: 'Email address not supplied',
          })
        );
        dispatch(workFailed());
        return;
      }
      dispatch(setCognitoEmailAddress(emailAddress));
      saveEmail(emailAddress);

      // Make API call
      const userDetails =
        await api.getCognitoUserIdByEmailAddressAsync(emailAddress);

      // Handle empty response
      if (isNullUndefinedOrEmptyObject(userDetails)) {
        dispatch(
          setErrorMessage({
            message: 'Email address does not match known user.',
          })
        );
        dispatch(workFailed());
        return;
      }

      // On succcess, update state
      dispatch(
        setCognitoUsername({
          cognitoUsername: `${getAPIManagerInstance().getPlaceUrl()}::userid/${userDetails.userId}`,
        })
      );
      dispatch(performSignInAsync());
    } catch (error) {
      dispatch(setErrorMessage({ message: error.message }));
      dispatch(workFailed());
    }
  };
};

export const performSignInAsync = (isAccountCreation = false) => {
  return async (dispatch, getState, _api) => {
    dispatch(setSendAuthCodeInProgress(true));

    let progressMessage =
      isAccountCreation === true
        ? 'Creating new account...'
        : 'Signing in user...';
    let errorMessage = '';
    dispatch(setProgressMessage({ message: progressMessage }));
    try {
      await signOut();
      const { auth } = getState();
      const { nextStep } = await signIn({
        username: auth.cognitoUsername,
        options: {
          authFlowType: 'CUSTOM_WITHOUT_SRP',
          clientId:
            awsConfig[process.env.REACT_APP_ENVIRONMENT].userPoolClientId,
          authParameters: {
            USERNAME: auth.cognitoUsername,
          },
          ClientMetadata: {},
        },
      });
      if (!nextStep) {
        errorMessage =
          isAccountCreation === true
            ? 'Unable to create new account!'
            : 'Unable to identify user by email address!';
        dispatch(
          setErrorMessage({
            message: errorMessage,
          })
        );
        dispatch(workFailed());
      } else {
        let progressMessage = isAccountCreation
          ? 'Validating user email...'
          : 'Validating user signin request...';
        dispatch(
          setProgressMessage({
            message: progressMessage,
          })
        );
        const challengeEmail = auth.cognitoEmailAddress || getEmail();

        // If there is no email address to use for login confirmation
        // login fails
        if (isNullUndefinedOrEmptyString(challengeEmail)) {
          dispatch(
            setErrorMessage({
              message:
                'Unexpected error occured while communicating with authorization server.',
            })
          );
          dispatch(workFailed());
        } else {
          const { nextStep } = await confirmSignIn({
            challengeResponse: challengeEmail.toLowerCase(),
          });

          // Handle unexpected result
          if (!nextStep) {
            dispatch(
              setErrorMessage({
                message:
                  'The email address was changed during authorization process.',
              })
            );
            dispatch(workFailed());
          } else {
            dispatch(setAuthCodeRequired(true));
          }
        }
      }
    } catch (err) {
      dispatch(
        setErrorMessage({
          message: 'Unknown error occurred while retrieving associated user.',
        })
      );
      dispatch(workFailed());
    } finally {
      dispatch(setSendAuthCodeInProgress(false));
    }
  };
};

export const submitAuthCode = (authCode, isAccountCreation = false) => {
  return async (dispatch, getState, api) => {
    dispatch(setSubmitAuthCodeInProgress(true));
    dispatch(
      setProgressMessage({
        message: 'Confirming authorization code...',
      })
    );
    try {
      const { nextStep } = await confirmSignIn({
        challengeResponse: authCode,
      });
      dispatch(setProgressMessage('Authorizing...'));
      // Handle failure here
      if (!nextStep) {
        dispatch(
          setErrorMessage({
            message: 'Unable to confirm authorization code.',
          })
        );
        dispatch(workFailed());
      } else {
        dispatch(
          setProgressMessage({
            message: 'Fetching user session...',
          })
        );
        // If successful, get the session
        const { tokens } = await fetchAuthSession();
        dispatch(setProgressMessage('Fetching user session...'));

        // Update the tokens in redux
        dispatch(setAccessToken(tokens.accessToken?.toString()));
        dispatch(setIdToken(tokens.idToken?.toString()));

        // Store in local storage
        saveTokens(tokens.accessToken?.toString(), tokens.idToken?.toString());

        // Get user details using access token
        const authEmail = getState().auth.cognitoEmailAddress || getEmail();
        try {
          const userDetailsResult = await api.getUserDetailsAuthTokenAsync(
            authEmail,
            tokens.idToken
          );
          dispatch(setUserDetails(userDetailsResult));
        } catch (error) {
          dispatch({ type: 'resetRegSystem' });
          dispatch(setErrorMessage({ message: error.message }));
          throw error;
        }

        // Persist in local storage
        saveEmail(authEmail);

        if (isAccountCreation === true) {
          dispatch(setIsAccountCreationCompleted(true));
        } else {
          dispatch(setIsUserLoginCompleted(true));
        }

        dispatch(workSucceeded());
        dispatch(setAuthCodeRequired(false));
      }
    } catch (err) {
      dispatch(
        setErrorMessage({
          message: 'Unknown error occurred while confirming auth code.',
        })
      );
      dispatch(workFailed());
      dispatch(setAuthCodeRequired(true));
    } finally {
      dispatch(setSubmitAuthCodeInProgress(false));
    }
  };
};

export const refreshSession = (forceRefresh = false) => {
  return async (dispatch, getState, api) => {
    const { tokens } = await fetchAuthSession({ forceRefresh: forceRefresh });
    const currentCognitoEmail = getState().auth.cognitoEmailAddress;

    if (tokens) {
      // Update the tokens in redux
      dispatch(setAccessToken(tokens.accessToken?.toString()));
      dispatch(setIdToken(tokens.idToken?.toString()));

      if (!currentCognitoEmail || currentCognitoEmail === '') {
        const storedEmail = getEmail();
        if (storedEmail) {
          try {
            const userDetailsResult = await api.getUserDetailsAuthTokenAsync(
              storedEmail,
              tokens.idToken
            );
            dispatch(setUserDetails(userDetailsResult));
          } catch (error) {
            dispatch({ type: 'resetRegSystem' });
            dispatch(setErrorMessage({ message: error.message }));
            throw error;
          }
        }
      }
      saveTokens(tokens.accessToken?.toString(), tokens.idToken?.toString());
    } else {
      dispatch(setAccessToken(undefined));
      dispatch(setIdToken(undefined));
      clearTokens();
      clearEmail();
    }
    return tokens;
  };
};

export const terminateSession = () => {
  return async (dispatch, getState, api) => {
    try {
      await signOut();
      clearTokens();
      clearEmail();
    } catch (error) {
      console.log(`Error signing out:`, error);
    }
  };
};

export const createAccount = (newAccountDetails) => {
  return async (dispatch, getState, api) => {
    const {
      email,
      firstName,
      lastName,
      phone,
      companyName,
      mailingAddress,
      mailingAddressComponents,
    } = newAccountDetails;

    dispatch(workStarted());
    dispatch(
      setProgressMessage({
        message: 'Creating account...',
      })
    );
    try {
      const preparedPayload = {
        email: email,
        firstName: firstName,
        lastName: lastName,
        companyName: companyName,
        mailingAddress: mailingAddress,
        phoneNumber: phone,
        misc: {
          mailingAddressComponents: mailingAddressComponents,
        },
      };
      let userDetails = await api.createAccountAsync(preparedPayload);
      if (userDetails.userId) {
        dispatch(
          setProgressMessage({
            message: 'Account created! Authorizing...',
          })
        );
        dispatch(workSucceeded());
        dispatch(
          setCognitoUsername({
            cognitoUsername: `${getAPIManagerInstance().getPlaceUrl()}::userid/${userDetails.userId}`,
          })
        );
        dispatch(setCognitoEmailAddress(userDetails.email));
        dispatch(performSignInAsync(userDetails.email, true));
      } else {
        dispatch(setErrorMessage({ message: 'Unable to create account.' }));
        dispatch(workFailed());
      }
    } catch (err) {
      dispatch(
        setErrorMessage({
          message:
            err?.message ??
            'An unexpected error occurred while creating the account.',
        })
      );
      dispatch(workFailed());
    }
  };
};

export const updateAccountDetails = (userID, updatedDetails) => {
  return async (dispatch, getState, api) => {
    const {
      email,
      firstName,
      lastName,
      phone,
      companyName,
      mailingAddress,
      mailingAddressComponents,
    } = updatedDetails;

    try {
      dispatch(workStarted());
      dispatch(
        setProgressMessage({
          message: 'updating account details...',
        })
      );

      const preparedPayload = {
        accessToken: getState().auth?.idToken,
        userId: userID,
        email: email,
        firstName: firstName,
        lastName: lastName,
        companyName: companyName,
        mailingAddress: mailingAddress,
        phoneNumber: phone,
        misc: {
          mailingAddressComponents: mailingAddressComponents,
        },
      };

      // Get user details using access token
      const result = await api.editPersonDetailsAsync(preparedPayload);

      if (result && result?.status === 200) {
        dispatch(workSucceeded());
        dispatch(setUserDetails(updatedDetails));
      } else {
        dispatch(
          setErrorMessage({ message: 'Unable to update account details.' })
        );
        dispatch(workFailed());
      }
    } catch (err) {
      dispatch(
        setErrorMessage({
          message:
            'Unknown error occurred while trying to update account details.',
        })
      );
      dispatch(workFailed());
    }
  };
};
